// src/components/ExamWindowApp.jsx
// Root of the separate exam window (opened via window.open from
// StudentDashboard.jsx at pathname '/exam'). Owns the session lifecycle -
// create/resume, navigation between screens, autosave, submit - and renders
// whichever screen the student is currently on.
//
// Phase 3a scope: everything happens in this one window (Exhibits/
// Requirements are placeholders, Responses = word processor only, no
// spreadsheet yet, no separate popup windows yet - those are Phase 3b/3c).

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { doc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { createSubmission, getAuthenticatedFileUrl } from '../firebase/submissions';
import { getExamHelpDocument, getExamHelpButtonVisible, getExamShowBreakdownScreen, getExamInteractiveSpreadsheetMarking } from '../firebase/settings';
import { getHeadMarkerName } from '../firebase/headMarkerNotifications';
import { DEFAULT_ORG_ID } from '../firebase/orgs';
import { generateExamPdf, numberSpreadsheets } from '../utils/generateExamPdf';
import { spreadsheetMarkingMode } from '../utils/spreadsheetMarkingMode';
import { buildExamGroups } from '../utils/examGroups';
import ExamSummaryScreen from './ExamSummaryScreen';
import ExamQuestionBreakdown from './ExamQuestionBreakdown';
import ExamSplitScreen from './ExamSplitScreen';
import ExamSubmitScreen from './ExamSubmitScreen';
import ExamNavigateModal from './ExamNavigateModal';
import { useWriteGuard } from '../utils/writeGuard';
import { installExamPasteGuard, PASTE_BLOCKED_MESSAGE } from '../utils/examClipboard';

const ExamWindowApp = ({ user }) => {
  const params = new URLSearchParams(window.location.search);
  const homeworkType = params.get('homeworkType');
  const homeworkName = params.get('homeworkName') || homeworkType;
  const cohortId = params.get('cohortId');
  const cohortName = params.get('cohortName') || '';
  // Head-marker-only walkthrough mode (see HeadMarkerDashboard's "Practice /
  // Demo Run" launcher and createPracticeExamSession) - same UI end to end,
  // but always untimed, no cohort/student involved, and finishes with a
  // local PDF download instead of a real submission. See handleSubmit below.
  const practiceMode = params.get('practice') === '1';
  // "Start Fresh" from the practice launcher - read once, then dropped from
  // the address bar (see the session effect below) so reloading the window
  // part-way through resumes that run rather than wiping it again.
  const practiceFreshRef = useRef(practiceMode && params.get('fresh') === '1');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState(null);
  // The cohort the session belongs to, per the server - can differ from the
  // URL's if the student has moved cohort since starting it
  const [sessionCohort, setSessionCohort] = useState({ cohortId, cohortName });
  const [status, setStatus] = useState('not_started');
  const [startedAtMs, setStartedAtMs] = useState(null);
  const [serverDeadlineMs, setServerDeadlineMs] = useState(null);
  const [unlimitedTime, setUnlimitedTime] = useState(false);
  const [examExtraTimeApplied, setExamExtraTimeApplied] = useState(null);
  const [durationMinutes, setDurationMinutes] = useState(null);
  const [sectionStructure, setSectionStructure] = useState(null);
  const [answers, setAnswers] = useState({});
  const [scratchPad, setScratchPad] = useState('');
  const [scratchPadUpdatedAt, setScratchPadUpdatedAt] = useState(null);
  const [exhibitMarks, setExhibitMarks] = useState({}); // { [materialId]: [{id,pageNumber,type,color,rects}] }
  const [navState, setNavState] = useState({ currentSectionId: null, screen: 'summary' });
  const [remainingMs, setRemainingMs] = useState(0);
  // True only for the brief window between navigating off the summary screen
  // and beginExamClock's server round-trip resolving - lets the timer badge
  // show "Starting timer..." instead of a misleading "00:00" while the
  // student can already see and start reading the actual question.
  const [clockStarting, setClockStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Submission genuinely takes a while (a Cloud Function round trip, then
  // client-side PDF generation, then the upload) - without this a student
  // just sees "Submitting..." sit there for up to a minute with no sign
  // anything is happening, which reads as broken. Purely cosmetic; doesn't
  // change the actual timing.
  const [submitStage, setSubmitStage] = useState('');
  // Holds the generated PDF after a successful submit so the student can
  // download it themselves via a real click (see the "submitted" screen
  // below) - triggering it automatically makes Safari show its "Allow
  // downloads?" permission dialog right after Submit with no context for
  // why, which is confusing. A genuine user-initiated click still shows
  // that dialog the first time on a given site, but at least now it's in
  // response to something the student did on purpose.
  const [submittedFile, setSubmittedFile] = useState(null); // { file, filename }
  // The student's spreadsheet answers, numbered as in the PDF, for the
  // "Download your spreadsheets (Excel)" button on the same screen
  const [submittedSheets, setSubmittedSheets] = useState(null);
  const [sheetsDownloading, setSheetsDownloading] = useState(false);
  // Shown for a few seconds when a paste from outside the exam is refused
  const [pasteNotice, setPasteNotice] = useState(false);
  const [showNavigate, setShowNavigate] = useState(false);
  const [helpDocumentUrl, setHelpDocumentUrl] = useState(null);
  const [showHelpButton, setShowHelpButton] = useState(true);
  // Whether this homework type has the standalone breakdown screen enabled
  // (Head Marker Dashboard > Settings > Homework) - off by default.
  const [showBreakdownScreen, setShowBreakdownScreen] = useState(false);
  // Used in the error messages below ("contact <name>") instead of a
  // hardcoded tutor name, since the platform now has more than one org.
  const [headMarkerName, setHeadMarkerName] = useState('your tutor');

  const submittedRef = useRef(false);
  const { guard, blocked: writeBlocked } = useWriteGuard();

  useEffect(() => {
    document.title = practiceMode ? `${homeworkName} - Practice Run` : `${homeworkName} - Exam`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeworkName]);

  // No pasting in from outside the exam (an answer written elsewhere, e.g.
  // by an AI) - copying and pasting within the exam, and copying out of it,
  // still work. See utils/examClipboard.js.
  useEffect(() => {
    if (status === 'submitted') return undefined;
    let timer;
    const uninstall = installExamPasteGuard(() => {
      setPasteNotice(true);
      clearTimeout(timer);
      timer = setTimeout(() => setPasteNotice(false), 4000);
    });
    return () => { uninstall(); clearTimeout(timer); };
  }, [status]);

  useEffect(() => {
    if (!user) return;
    getHeadMarkerName(user.orgId || DEFAULT_ORG_ID).then(setHeadMarkerName);
  }, [user]);

  // Surfaces via the existing timerError banner on the breakdown/split-screen
  // views - see useWriteGuard's comment for why this should never trigger
  // for real usage, only a genuine runaway-write bug.
  useEffect(() => {
    if (writeBlocked) {
      setError('Something went wrong and this window has stopped saving your navigation/flags. Your question answers in separate Word Processor/Spreadsheet windows are unaffected - please close this window, reopen the exam, and check your progress.');
    }
  }, [writeBlocked]);

  // Resolve the one global Tax Tables / Help document, if one has been uploaded
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [helpDoc, helpVisible] = await Promise.all([getExamHelpDocument(), getExamHelpButtonVisible()]);
        if (cancelled) return;
        setShowHelpButton(helpVisible);
        if (!helpDoc) return;
        const url = await getAuthenticatedFileUrl(helpDoc.filePath);
        if (!cancelled) setHelpDocumentUrl(url);
      } catch (err) {
        console.error('Failed to load help document:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Resolve whether this homework type has the standalone breakdown screen
  // switched on
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const showBreakdownScreens = await getExamShowBreakdownScreen();
        if (!cancelled) setShowBreakdownScreen(!!showBreakdownScreens[homeworkType]);
      } catch (err) {
        console.error('Failed to load exam breakdown screen setting:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [homeworkType]);

  // Create or resume the session on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const functions = getFunctions(getApp());
        const fresh = practiceFreshRef.current;
        practiceFreshRef.current = false;
        if (fresh) {
          const url = new URL(window.location.href);
          url.searchParams.delete('fresh');
          window.history.replaceState(null, '', url.toString());
        }
        const result = practiceMode
          ? await httpsCallable(functions, 'createPracticeExamSession')({ homeworkType, fresh })
          : await httpsCallable(functions, 'createOrResumeExamSession')({
              studentName: user.name,
              studentEmail: user.email,
              studentPhone: user.phone || '',
              cohortId,
              cohortName,
              homeworkType
            });
        if (cancelled) return;

        const data = result.data;
        setSessionId(data.sessionId);
        if (data.cohortId) setSessionCohort({ cohortId: data.cohortId, cohortName: data.cohortName || '' });
        setStatus(data.status);
        setStartedAtMs(data.startedAt);
        setServerDeadlineMs(data.serverDeadline);
        setUnlimitedTime(!!data.unlimitedTime);
        setExamExtraTimeApplied(data.examExtraTimeApplied || null);
        setDurationMinutes(data.durationMinutes);
        setSectionStructure(data.sectionStructureSnapshot);
        setAnswers(data.answers || {});
        setScratchPad(data.scratchPad || '');
        setExhibitMarks(data.exhibitMarks || {});
        setNavState(data.navState || { currentSectionId: data.sectionStructureSnapshot?.sections?.[0]?.id, screen: 'summary' });
      } catch (err) {
        console.error('Failed to start/resume exam session:', err);
        if (!cancelled) {
          // Not yet released (see createOrResumeExamSession) - its message
          // already says when it opens, so show that instead of the generic one.
          setError(err?.code === 'functions/failed-precondition' && err.message
            ? err.message
            : `Unable to start this exam. Please close this window and try again, or contact ${headMarkerName}.`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeworkType, cohortId]);

  // Live sync: answers are now edited in separate Word Processor/Spreadsheet
  // popup windows, so this window needs to pick up their autosaves in real
  // time (for completion indicators, the submit screen, and the PDF at
  // submit time) rather than owning a local copy itself.
  useEffect(() => {
    if (!sessionId) return;
    const unsub = onSnapshot(doc(db, 'examSessions', sessionId), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setAnswers(data.answers || {});
      setScratchPad(data.scratchPad || '');
      setScratchPadUpdatedAt(data.scratchPadUpdatedAt || null);
      setExhibitMarks(data.exhibitMarks || {});
      if (data.status) setStatus(data.status);
    });
    return unsub;
  }, [sessionId]);

  const persistNav = useCallback((next) => {
    setNavState(next);
    if (sessionId) {
      guard(() => updateDoc(doc(db, 'examSessions', sessionId), { navState: next }))
        .catch(err => console.error('Nav save failed:', err));
    }
  }, [sessionId, guard]);

  const handleSubmit = useCallback(async (auto = false) => {
    if (submittedRef.current || !sessionId) return;
    submittedRef.current = true;
    setSubmitting(true);
    // Tracked alongside setSubmitStage (rather than read back from state)
    // so the catch block below knows exactly which stage failed even
    // though React state updates from earlier in this same call aren't
    // guaranteed to have landed yet.
    let stage = 'Finalizing your session...';
    setSubmitStage(stage);
    setError('');

    try {
      const functions = getFunctions(getApp());
      const finalizeExamSession = httpsCallable(functions, 'finalizeExamSession');
      const submittedAtMs = Date.now();

      // The Cloud Function round trip (which can be slow on a cold start)
      // and the client-side PDF render don't depend on each other's result -
      // only the upload afterwards needs both to have succeeded - so running
      // them together instead of one after the other shaves the PDF's
      // render time off the total wait.
      stage = 'Preparing your submission...';
      setSubmitStage(stage);
      const examSession = {
        studentName: practiceMode ? `${user.name} (Practice Run)` : user.name,
        cohortName: sessionCohort.cohortName,
        homeworkType,
        sectionStructureSnapshot: sectionStructure,
        answers,
        startedAtMs,
        submittedAtMs
      };
      // `studentFile` is always the full answer, grids included - it's the
      // student's own copy below, and the submitted file too unless the
      // tutor marks spreadsheets as spreadsheets only
      const [finalized, studentFile, interactiveSpreadsheetMarking] = await Promise.all([
        finalizeExamSession({ sessionId }),
        generateExamPdf(examSession, homeworkName),
        getExamInteractiveSpreadsheetMarking()
      ]);

      // Off by default (see HeadMarkerDashboard Settings > Homework Types >
      // Interactive Spreadsheet Marking) - when off, nothing extra is
      // captured or written, so submissions behave exactly as before this
      // feature existed. Only pulls gridJson/blankGridJson out of the groups
      // that actually have a spreadsheet answer, numbered Spreadsheet 1, 2...
      let spreadsheetAnswers;
      let file = studentFile;
      const markingMode = spreadsheetMarkingMode(interactiveSpreadsheetMarking?.[homeworkType]);
      const groups = buildExamGroups(sectionStructure);
      const sheetNumbers = numberSpreadsheets(groups, answers);
      const captured = {};
      groups.forEach((group) => {
        const groupAnswer = answers?.[group.id];
        const gridJson = groupAnswer?.spreadsheet?.gridJson;
        const blankGridJson = groupAnswer?.blankSpreadsheet?.gridJson;
        const numbers = sheetNumbers[group.id] || {};
        if (gridJson || blankGridJson) {
          captured[group.id] = {
            name: group.name,
            ...(gridJson ? { gridJson } : {}),
            ...(blankGridJson ? { blankGridJson } : {}),
            ...(numbers.spreadsheet ? { number: numbers.spreadsheet } : {}),
            ...(numbers.blankSpreadsheet ? { blankNumber: numbers.blankSpreadsheet } : {})
          };
        }
      });
      // The student's own Excel copy, whatever the marking setting
      const ownSheets = Object.keys(sheetNumbers).length > 0
        ? JSON.parse(JSON.stringify(captured))
        : null;
      if (markingMode !== 'pdf') {
        if (Object.keys(captured).length > 0) {
          spreadsheetAnswers = captured;
        }
        // Spreadsheet only: the submitted PDF has a "See Spreadsheet N" note
        // where each grid would be, and records the page each note is on so
        // the marked PDF can put the marked spreadsheet straight after it
        if (spreadsheetAnswers && markingMode === 'spreadsheet' && Object.keys(sheetNumbers).length > 0) {
          stage = 'Preparing your submission...';
          const pageOf = {};
          file = await generateExamPdf({ ...examSession, spreadsheetNotes: true, pageOf }, homeworkName);
          Object.entries(spreadsheetAnswers).forEach(([groupId, entry]) => {
            const page = pageOf[`${groupId}:spreadsheet`];
            const blankPage = pageOf[`${groupId}:blankSpreadsheet`];
            if (page) entry.pdfPage = page;
            if (blankPage) entry.blankPdfPage = blankPage;
          });
        }
      }

      stage = 'Uploading...';
      setSubmitStage(stage);
      if (practiceMode) {
        // No real student, no submission, no queue entry - the PDF is
        // offered as a click-to-download button on the "submitted" screen
        // instead of being auto-triggered (see submittedFile above).
        setSubmittedFile({ file: studentFile, filename: `${homeworkName.replace(/[^a-z0-9]+/gi, '_')}_practice.pdf` });
        setSubmittedSheets(ownSheets);

        await updateDoc(doc(db, 'examSessions', sessionId), {
          status: 'submitted',
          submittedAt: serverTimestamp(),
          finalizedBy: 'head_marker'
        });

        setStatus('submitted');
        return;
      }

      const submitCohort = finalized?.data?.cohortId
        ? { cohortId: finalized.data.cohortId, cohortName: finalized.data.cohortName || '' }
        : sessionCohort;
      const { id: submissionId } = await createSubmission({
        studentId: user.uid,
        studentName: user.name,
        studentEmail: user.email,
        studentPhone: user.phone || '',
        cohortId: submitCohort.cohortId,
        cohortName: submitCohort.cohortName,
        homeworkType,
        file,
        examDurationMinutes: durationMinutes,
        examExtraTimeApplied,
        submittedVia: 'exam',
        ...(spreadsheetAnswers ? { spreadsheetAnswers, spreadsheetMarkingMode: file === studentFile ? 'both' : 'spreadsheet' } : {})
      });

      // Give the student their own copy of the generated PDF, offered as a
      // click-to-download button on the "submitted" screen (see
      // submittedFile above) rather than auto-triggered.
      setSubmittedFile({ file: studentFile, filename: `${homeworkName.replace(/[^a-z0-9]+/gi, '_')}_YourSubmission.pdf` });
      setSubmittedSheets(ownSheets);

      await updateDoc(doc(db, 'examSessions', sessionId), {
        status: 'submitted',
        submittedAt: serverTimestamp(),
        resultingSubmissionId: submissionId,
        finalizedBy: auto ? 'timeout_client' : 'student'
      });

      try {
        const { sendDirectNotification, NOTIFICATION_TYPES } = await import('../firebase/headMarkerNotifications');
        sendDirectNotification(NOTIFICATION_TYPES.NEW_HOMEWORK_SUBMITTED, {
          studentName: user.name,
          cohortName: submitCohort.cohortName,
          homeworkType: homeworkName,
          submittedAt: new Date().toLocaleString('en-GB', { timeZone: 'Europe/London' })
        });
      } catch (e) { console.error('Notification error:', e); }

      setStatus('submitted');
    } catch (err) {
      console.error('Exam submit failed:', err);
      submittedRef.current = false;
      setSubmitting(false);
      setSubmitStage('');
      setError(
        err.message && err.message.includes('expired')
          ? `Time has expired for this exam and it could not be submitted. Please contact ${headMarkerName}.`
          // No deadline set for this homework (see createSubmission) - the
          // answers are still saved, so it can go once one is set
          : err.message && err.message.includes('homeworkDeadline')
            ? `This homework doesn't have a deadline set yet, so it can't be submitted. Your answers are saved. Please contact ${headMarkerName}, then submit again once they've set one.`
          // Already handed in another way (see finalizeExamSession) - its message says so
          : err?.code === 'functions/failed-precondition' && err.message
            ? err.message
            : `Submitting your exam failed. Please try again or contact ${headMarkerName} if the problem persists.`
      );

      // Best-effort - the on-screen message alone tells no one which step
      // broke or why, and console.error is invisible unless someone's
      // watching the student's browser live at the exact moment (see the
      // "Submitting your exam failed" investigation, 2026-09-14). The
      // session is still 'in_progress' at this point (only a successful
      // submit ever flips its status), so the student can still write to
      // it under firestore.rules. Visible to markers/head markers via the
      // examSessions doc in the Firebase console.
      try {
        await updateDoc(doc(db, 'examSessions', sessionId), {
          lastSubmitError: {
            message: String(err?.message || err),
            stage,
            auto,
            at: serverTimestamp()
          }
        });
      } catch (logErr) {
        console.error('Failed to record submit error:', logErr);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, answers, sectionStructure, startedAtMs, user, homeworkType, homeworkName, sessionCohort, durationMinutes, examExtraTimeApplied, headMarkerName]);

  const handleDownloadCopy = useCallback(() => {
    if (!submittedFile) return;
    const url = URL.createObjectURL(submittedFile.file);
    const link = document.createElement('a');
    link.href = url;
    link.download = submittedFile.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [submittedFile]);

  const handleDownloadSheets = async () => {
    if (!submittedSheets) return;
    setSheetsDownloading(true);
    try {
      const { downloadMarkedSpreadsheetExcel } = await import('../utils/studentSpreadsheetMarking');
      await downloadMarkedSpreadsheetExcel(submittedSheets, null, `${homeworkName.replace(/[^a-z0-9]+/gi, '_')}_YourSpreadsheets.xlsx`);
    } catch (err) {
      console.error('Spreadsheet download failed:', err);
      alert('Sorry, your spreadsheets could not be downloaded. Please try again.');
    } finally {
      setSheetsDownloading(false);
    }
  };

  // Countdown + auto-submit at zero (only once the clock has actually started)
  useEffect(() => {
    if (status !== 'in_progress' || !serverDeadlineMs) return;
    const tick = () => {
      const remaining = serverDeadlineMs - Date.now();
      setRemainingMs(remaining);
      if (remaining <= 0 && !submittedRef.current) handleSubmit(true);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [status, serverDeadlineMs, handleSubmit]);

  const toggleFlag = (sectionId) => {
    const next = !answers[sectionId]?.flagged;
    setAnswers(prev => ({ ...prev, [sectionId]: { ...prev[sectionId], flagged: next } }));
    if (sessionId) {
      guard(() => updateDoc(doc(db, 'examSessions', sessionId), { [`answers.${sectionId}.flagged`]: next }))
        .catch(err => console.error('Flag update failed:', err));
    }
  };

  const handleBeginClock = () => {
    // Move on immediately (local state only) - the question, exhibits etc.
    // don't need to wait on the server round-trip below. The Firestore write
    // is deliberately deferred: the security rule only allows a student to
    // update their session once status is 'in_progress', which beginExamClock
    // (Admin SDK, bypasses rules) hasn't set yet at this point - writing now
    // would just be rejected. Only the timer display itself (which genuinely
    // has to be server-authoritative) catches up a moment later, via the
    // "Starting timer..." state on the next screen.
    const nextNav = { ...navState, screen: screenForGroup(currentGroup) };
    setNavState(nextNav);
    if (!unlimitedTime) setClockStarting(true);

    (async () => {
      try {
        const functions = getFunctions(getApp());
        const beginExamClock = httpsCallable(functions, 'beginExamClock');
        const result = await beginExamClock({ sessionId });
        setStartedAtMs(result.data.startedAt);
        setServerDeadlineMs(result.data.serverDeadline);
        // Exhibits/requirements changed since the student first opened the
        // homework - beginExamClock has swapped in the current ones
        if (result.data.sectionStructureSnapshot) setSectionStructure(result.data.sectionStructureSnapshot);
        // beginExamClock doesn't rewrite unlimitedTime - already known from the
        // create/resume call above.
        setStatus('in_progress');
        setClockStarting(false);
        // Now safe to persist nav - status is 'in_progress' server-side as of
        // the call just above.
        if (sessionId) {
          updateDoc(doc(db, 'examSessions', sessionId), { navState: nextNav })
            .catch(err => console.error('Nav save failed:', err));
        }
      } catch (err) {
        console.error('Failed to start exam clock:', err);
        // Deliberately leave clockStarting true rather than falling through to
        // a misleading "00:00" - the student can already read/answer the
        // question, they just can't be timed yet.
        setError(`Unable to start the timer. Please refresh this window, or contact ${headMarkerName} if it keeps happening.`);
      }
    })();
  };

  // A "group" is one navigable exam question - either a single marking
  // section, or several sections combined (e.g. Question 2's parts (i) and
  // (ii) sharing one screen and one answer) - see src/utils/examGroups.js.
  // navState.currentSectionId still stores a real section id (the current
  // group's first member) purely as a stable pointer; the answers doc itself
  // is keyed by group id, not section id.
  const groups = buildExamGroups(sectionStructure);
  const currentGroupIndex = groups.findIndex(g => g.sections.some(s => s.id === navState.currentSectionId));
  const currentGroup = groups[currentGroupIndex] || groups[0];

  // The homework-type toggle turns the Breakdown Screen on at all; each
  // question can then individually opt out via its own showBreakdownScreen
  // (default shown) - e.g. a question sharing content with the one before it
  // that doesn't need its own intro screen repeated.
  const screenForGroup = (g) => (showBreakdownScreen && g && g.showBreakdownScreen !== false) ? 'breakdown' : 'answering';

  const goToGroup = (groupId) => {
    const target = groups.find(g => g.id === groupId);
    const targetSectionId = target ? target.sections[0].id : groupId;
    persistNav({ currentSectionId: targetSectionId, screen: screenForGroup(target) });
    setShowNavigate(false);
  };

  const handleNextFromBreakdown = () => persistNav({ ...navState, screen: 'answering' });

  const handleNextFromSplitScreen = () => {
    const nextIndex = currentGroupIndex + 1;
    if (nextIndex < groups.length) {
      persistNav({ currentSectionId: groups[nextIndex].sections[0].id, screen: screenForGroup(groups[nextIndex]) });
    } else {
      persistNav({ ...navState, screen: 'final' });
    }
  };

  const handlePreviousFromSplitScreen = () => {
    const prevIndex = currentGroupIndex - 1;
    if (prevIndex >= 0) {
      persistNav({ currentSectionId: groups[prevIndex].sections[0].id, screen: screenForGroup(groups[prevIndex]) });
    }
  };

  // From the final review screen, straight back into the last question - so a
  // student who isn't ready to submit yet can keep adding to their work
  // instead of only being able to jump to a specific section from the list.
  const handleBackFromFinal = () => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup) persistNav({ currentSectionId: lastGroup.sections[0].id, screen: 'answering' });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#718096', fontFamily: 'sans-serif' }}>
        Starting your task...
      </div>
    );
  }

  if (error && !sessionId) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#991b1b', fontFamily: 'sans-serif' }}>
        {error}
      </div>
    );
  }

  if (status === 'submitted') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', flexDirection: 'column', gap: '16px', fontFamily: 'sans-serif' }}>
        <div style={{ fontSize: '20px', fontWeight: '700', color: '#1b998b' }}>
          {practiceMode ? 'Practice run complete' : 'Exam submitted successfully'}
        </div>
        <div style={{ color: '#718096' }}>
          {practiceMode
            ? `You can download a PDF copy below${submittedSheets ? ' (and the spreadsheets as an Excel file)' : ''}, then close this window.`
            : `Your submission has been received. You can download your own copy below${submittedSheets ? ' (your spreadsheets as an Excel file too)' : ''}, then close this window.`}
        </div>
        {submittedFile && (
          <>
            <button
              type="button"
              onClick={handleDownloadCopy}
              style={{
                padding: '12px 24px', background: '#667eea', color: 'white', border: 'none',
                borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '15px'
              }}
            >
              Download your copy (PDF)
            </button>
            {submittedSheets && (
              <button
                type="button"
                onClick={handleDownloadSheets}
                disabled={sheetsDownloading}
                style={{
                  padding: '12px 24px', background: 'white', color: '#667eea', border: '2px solid #667eea',
                  borderRadius: '8px', cursor: sheetsDownloading ? 'default' : 'pointer', fontWeight: '700', fontSize: '15px'
                }}
              >
                {sheetsDownloading ? 'Preparing...' : 'Download your spreadsheets (Excel)'}
              </button>
            )}
            <div style={{ color: '#a0aec0', fontSize: '13px', maxWidth: '320px', textAlign: 'center' }}>
              Your browser may ask permission to download files - tap "Allow".
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9', fontFamily: 'sans-serif' }}>
      {navState.screen === 'summary' && (
        <ExamSummaryScreen
          homeworkName={homeworkName}
          summary={sectionStructure?.examSummary}
          unlimitedTime={unlimitedTime}
          onNext={handleBeginClock}
        />
      )}

      {navState.screen === 'breakdown' && currentGroup && (
        <ExamQuestionBreakdown
          group={currentGroup}
          sectionStructure={sectionStructure}
          sectionNumber={currentGroupIndex + 1}
          totalSections={groups.length}
          remainingMs={remainingMs}
          unlimitedTime={unlimitedTime}
          clockStarting={clockStarting}
          timerError={error}
          onNext={handleNextFromBreakdown}
        />
      )}

      {navState.screen === 'answering' && currentGroup && (
        <ExamSplitScreen
          sessionId={sessionId}
          group={currentGroup}
          sectionStructure={sectionStructure}
          sectionNumber={currentGroupIndex + 1}
          totalSections={groups.length}
          remainingMs={remainingMs}
          unlimitedTime={unlimitedTime}
          clockStarting={clockStarting}
          timerError={error}
          answer={answers[currentGroup.id]}
          scratchPad={scratchPad}
          scratchPadUpdatedAt={scratchPadUpdatedAt}
          exhibitMarks={exhibitMarks}
          onToggleFlag={() => toggleFlag(currentGroup.id)}
          onNext={handleNextFromSplitScreen}
          onPrevious={currentGroupIndex > 0 ? handlePreviousFromSplitScreen : null}
          onNavigate={() => setShowNavigate(true)}
          submitting={submitting}
          helpDocumentUrl={helpDocumentUrl}
          showHelpButton={showHelpButton}
        />
      )}

      {navState.screen === 'final' && (
        <ExamSubmitScreen
          homeworkName={homeworkName}
          groups={groups}
          answers={answers}
          submitting={submitting}
          submitStage={submitStage}
          error={error}
          onGoToSection={goToGroup}
          onSubmit={() => handleSubmit(false)}
          onBack={handleBackFromFinal}
        />
      )}

      {pasteNotice && (
        <div role="alert" style={{
          position: 'fixed', top: '16px', left: '50%', transform: 'translateX(-50%)', zIndex: 10000,
          background: '#2d3748', color: 'white', padding: '12px 20px', borderRadius: '8px',
          fontSize: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.25)', maxWidth: '90vw', textAlign: 'center'
        }}>
          {PASTE_BLOCKED_MESSAGE}
        </div>
      )}

      {showNavigate && (
        <ExamNavigateModal
          groups={groups}
          answers={answers}
          currentGroupId={currentGroup?.id}
          onSelect={goToGroup}
          onClose={() => setShowNavigate(false)}
        />
      )}
    </div>
  );
};

export default ExamWindowApp;
